import { z } from "zod";

/**
 * What the author wrote for a WhatsApp template, as the editor holds it and
 * `cep_whatsapp_template_locales.editor_state` stores it.
 *
 * Provider-neutral: this is the authored shape, not how Twilio takes it. The
 * translation into a `whatsapp/card` (or a bare `twilio/text`) and the
 * assignment of `{{n}}` positions happen in `whatsapp/template-compile.ts`.
 */

export const WHATSAPP_HEADER_TEXT_MAX = 60;
export const WHATSAPP_BODY_MAX = 1024;
export const WHATSAPP_FOOTER_MAX = 60;
export const WHATSAPP_BUTTON_LABEL_MAX = 20;
export const WHATSAPP_COPY_CODE_MAX = 20;
export const WHATSAPP_URL_MAX = 2000;
export const WHATSAPP_MAX_BUTTONS = 10;

export const WHATSAPP_MAX_URL_BUTTONS = 2;
export const WHATSAPP_MAX_CALL_BUTTONS = 1;

/**
 * Twilio requires a title on a copy code action, but WhatsApp draws its own
 * fixed label, so the author never sets one.
 */
export const WHATSAPP_COPY_CODE_TITLE = "Copy code";

export const WHATSAPP_HEADER_MAX_VARIABLES = 1;
export const WHATSAPP_MAX_VARIABLES = 100;
export const WHATSAPP_MAX_EMOJIS = 10;

const headerSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("text"), text: z.string() }),
  z.object({ kind: z.literal("image"), url: z.string() }),
]);

const buttonIdSchema = z.string().uuid();
const buttonSchema = z.discriminatedUnion("type", [
  z.object({
    id: buttonIdSchema,
    type: z.literal("link"),
    label: z.string(),
    /** A variable is allowed only as a trailing path suffix, never the domain. */
    url: z.string(),
  }),
  z.object({
    id: buttonIdSchema,
    type: z.literal("phone"),
    label: z.string(),
    phone: z.string(),
  }),
  z.object({
    id: buttonIdSchema,
    type: z.literal("copy_code"),
    /** Liquid, so each recipient can be sent their own code. */
    code: z.string(),
  }),
]);

/**
 * Lengths are deliberately not capped here. The editor saves as the author
 * types, so an over-long body has to round-trip; compile is where it becomes
 * an error the author sees.
 */
export const whatsAppTemplateContentSchema = z.object({
  header: headerSchema.nullable().default(null),
  /** Liquid. The one component WhatsApp always requires. */
  body: z.string(),
  /** Liquid is not permitted here; Meta refuses a footer with variables. */
  footer: z.string().nullable().default(null),
  buttons: z.array(buttonSchema).default([]),
  /**
   * The example value Meta reviews each variable with, keyed by the author's
   * variable name. Authored content, not a compile artifact: positions move
   * whenever the copy around a variable changes, so keying these by position
   * would lose the author's examples on the next edit.
   */
  samples: z.record(z.string(), z.string()).default({}),
});

/**
 * The parsed shape, so every field is present: the schema's defaults fill in
 * whatever the stored row left out.
 */
export type WhatsAppTemplateContent = z.infer<
  typeof whatsAppTemplateContentSchema
>;

/** An empty template, for a locale that has no content yet. */
export function emptyWhatsAppTemplateContent(): WhatsAppTemplateContent {
  return { header: null, body: "", footer: null, buttons: [], samples: {} };
}
